import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";

import { Cancellation } from "@/api";
import { AuthService, UserService } from "@/services";
import { type LoginResponse, type SearchViewType, type User, UserType } from "@/types";
import type { Login, SignUp } from "@/validation";

import { resetAllStores } from "./refresh";

interface State {
  status: "initial" | "loading" | "loaded" | "failed";
  userData: User;
}

const initialUserData: User = {
  username: "",
  companyName: "",
  paying: false,
  freeSearchesLeft: null,
  credentials: {
    authenticated: false,
    ordbogenAccount: false,
    type: UserType.Free,
    enterpriseType: null,
    uniloginFunction: null,
    openNetwork: {
      active: false,
      name: ""
    }
  },
  featureFlags: {
    availableSearchViews: ["classic"],
    availableWriteAssistantPromotion: null,
    hasLanguageChat: false,
    showSearchViewToggle: false,
    showPlusLink: false,
    showAbcPromotion: false,
    showCompanyDictionaryAd: false
  },
  settings: {
    compactArticlesExpanded: false,
    preferredSearchView: "classic"
  },
  system: {
    os: "windows"
  }
};

const STORE_ID = "user";

export const useUserStore = defineStore(STORE_ID, () => {
  const state = reactive<State>({ status: "initial", userData: initialUserData });

  const getUser = async () => {
    if (state.status !== "loaded") {
      await fetchUserData();
    }

    return state.userData;
  };

  const fetchUserData = async (): Promise<void> => {
    const prevStatus = state.status;

    try {
      state.status = "loading";

      state.userData = await UserService.getUser();

      state.status = "loaded";
    } catch (e) {
      if (e instanceof Cancellation) {
        state.status = prevStatus;
      }

      state.status = "failed";
      throw e;
    }
  };

  const signUp = async (form: SignUp): Promise<void> => {
    await AuthService.signUp(form);

    // Reset all stores, as we can't rely on the data anymore as they could be filled with user-related data
    resetAllStores();

    // Load the new user
    fetchUserData();
  };

  const login = (form: Login, backUrl?: string): Promise<LoginResponse> => AuthService.login(form, backUrl);

  const isSignedIn = computed(() =>
    ["company", "uni-login", "wayf", "oidc", "private"].includes(state.userData.credentials.type)
  );

  const isEnterpriseAdmin = computed(() => state.userData.credentials.enterpriseType === "admin");
  const isEnterpriseDictionaryEditor = computed(
    () => state.userData.credentials.enterpriseType === "dictionary-editor"
  );

  const setCompactArticlesExpanded = async (isExpanded: boolean) => {
    const previousCompactArticlesExpanded = state.userData.settings.compactArticlesExpanded;

    state.userData.settings.compactArticlesExpanded = isExpanded;
    try {
      await UserService.setCompactArticlesExpanded(isExpanded);
    } catch (e) {
      state.userData.settings.compactArticlesExpanded = previousCompactArticlesExpanded;
      throw e;
    }
  };

  const setPreferredSearchView = async (preferredSearchView: SearchViewType) => {
    const previousPreferredSearchView = state.userData.settings.preferredSearchView;

    state.userData.settings.preferredSearchView = preferredSearchView;
    try {
      await UserService.setPreferredSearchView(preferredSearchView);
    } catch (e) {
      state.userData.settings.preferredSearchView = previousPreferredSearchView;
      throw e;
    }
  };

  return {
    ...toRefs(state),
    getUser,
    fetchUserData,
    login,
    signUp,
    isSignedIn,
    isEnterpriseAdmin,
    isEnterpriseDictionaryEditor,
    setCompactArticlesExpanded,
    setPreferredSearchView
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
